/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import type { FC } from 'react';
import React, { useCallback, useContext } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { styled } from '@compiled/react';
import type { LoomVideo } from '@loomhq/record-sdk';

import { IconButton } from '@atlaskit/button/new';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Tooltip from '@atlaskit/tooltip';

import { fg } from '@confluence/feature-gating';
import { withFlags } from '@confluence/flags/entry-points/withFlags';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import type { OpenLoomRecorderParams } from '@confluence/loom-utils/entry-points/useLoomRecorderEntryPoint';
import { useLoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariant';
import { useLoomRecorderEntryPoint } from '@confluence/loom-utils/entry-points/useLoomRecorderEntryPoint';
import { PageHeaderLoomButtonSpotlight } from '@confluence/loom-onboarding/entry-points/PageHeaderLoomButtonSpotlight';
import { useSkippableCoordination } from '@confluence/skippable-coordination-client';
import { useEditPageLoadingActions } from '@confluence/editor-loading-context/entry-points/EditPageLoadingContext';
import { getUrlForContentType } from '@confluence/content-types-utils/entry-points/getUrlForContentType';
import { LOOM_PAGE_HEADER_CONTENT_INSERTION } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout';
import type { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';

import { useLoomInsertUrlState } from '@confluence/loom-insert-url-store';
import { expVal } from '@confluence/feature-experiments';

import type {
	PageHeaderLoomButtonQuery as PageHeaderLoomButtonQuery$data,
	PageHeaderLoomButtonQueryVariables as PageHeaderLoomButtonQuery$variables,
} from './__types__/PageHeaderLoomButtonQuery';
import { LoomVideoIcon } from './VideoIcon';

type PageHeaderLoomButtonComponentProps = {
	contentId: string;
	dataVC?: string;
	label: string;
	spaceKey: string;
	contentType: string;
	flags: FlagsStateContainer;
	ssrPlaceholderIdProp?: ReturnType<typeof useSSRPlaceholderReplaceIdProp>;
	isCircle?: boolean;
	testId?: string;
	source?: string;
	isDisabled?: boolean;
};

export enum PostLoomRecordingCTA {
	EDITOR_INSERT = 'editorInsert',
	PAGE_COMMENT_INSERT = 'pageCommentInsert',
	COPY_LINK = 'copyLink',
	OPEN_ON_LOOM = 'openOnLoom',
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonSpacer = styled.div({
	minWidth: '30px',
});

const PageHeaderLoomButtonComponent: FC<PageHeaderLoomButtonComponentProps> = ({
	contentId,
	dataVC,
	label,
	spaceKey,
	contentType,
	flags,
	ssrPlaceholderIdProp,
	isCircle = false,
	testId,
	source = 'pageHeader',
	isDisabled = false,
}) => {
	const { userId } = useSessionData();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { loadEditor } = useEditPageLoadingActions();
	// eslint-disable-next-line confluence-sweet-state/sweet-state-prefer-actions-hook
	const [, loomInsertUrlActions] = useLoomInsertUrlState();
	const pageMode = useGetPageMode();

	const saveLoomUrlAndLoadEditor = (video: LoomVideo) => {
		loomInsertUrlActions.updateFullPageEditorLoomUrl(video);
		const redirectUrl = getUrlForContentType({
			contentType,
			spaceKey,
			contentId,
			redirectToEditor: true,
		});
		loadEditor({
			contentId,
			spaceKey,
			redirectUrl,
			contentType,
		});
	};

	const beginExperienceTracker = (postLoomRecordingCTA: PostLoomRecordingCTA) => {
		experienceTracker.start({
			name: LOOM_PAGE_HEADER_CONTENT_INSERTION,
			timeout: ExperienceTimeout.LOOM_PAGE_HEADER_CONTENT_INSERTION,
			attributes: { postLoomRecordingCTA },
		});
	};

	const { entryPointVariant } = useLoomEntryPointVariant();

	const { data } = useQuery<PageHeaderLoomButtonQuery$data, PageHeaderLoomButtonQuery$variables>(
		graphql`
			query PageHeaderLoomButtonQuery($contentId: ID!) {
				contentContributors(id: $contentId) {
					isCurrentUserContributor
				}
				content(id: $contentId, status: ["current", "draft"]) {
					nodes {
						id
						operations {
							operation
							targetType
						}
						history {
							ownedBy {
								... on KnownUser {
									accountId
								}
							}
						}
					}
				}
			}
		`,
		{
			variables: { contentId },
		},
	);

	let openLoomRecorderParams: OpenLoomRecorderParams | undefined;
	let postLoomRecordingCTA: PostLoomRecordingCTA = PostLoomRecordingCTA.OPEN_ON_LOOM;
	let resetInsertFunctionOnUnmount = false;

	const contentData = data?.content?.nodes?.[0];

	const isContentContributor =
		data?.contentContributors?.isCurrentUserContributor ||
		// isCurrentUserContributor is false for unpublished drafts, so we also check the owner
		(contentData?.history?.ownedBy as any)?.accountId === userId;

	const canEdit = contentData?.operations?.some(
		(operation) => operation?.operation === 'update' && operation?.targetType === contentType,
	);
	const canComment = contentData?.operations?.some(
		(operation) => operation?.targetType === 'comment' && operation?.operation === 'create',
	);

	if (canEdit && isContentContributor && contentType !== 'database') {
		// editor mode insert
		postLoomRecordingCTA = PostLoomRecordingCTA.EDITOR_INSERT;
		openLoomRecorderParams = {
			onInsert: (_, video: LoomVideo) => {
				beginExperienceTracker(postLoomRecordingCTA);
				saveLoomUrlAndLoadEditor(video);
			},
			// intentionally not localized because Loom RecordSDK does not support i18n
			insertButtonText: 'Insert Loom video',
		};
	} else if (canComment && contentType !== 'database') {
		// page comment insert
		postLoomRecordingCTA = PostLoomRecordingCTA.PAGE_COMMENT_INSERT;
		// Set to true so that CTA 'Add to comment' will not render if user has navigated to
		// different page post Loom recording.
		resetInsertFunctionOnUnmount = true;
		openLoomRecorderParams = {
			onInsert: (_, video: LoomVideo) => {
				beginExperienceTracker(postLoomRecordingCTA);
				loomInsertUrlActions.updatePageCommentEditorLoomUrl(video);
				const openPageCommentEditorButton = document.getElementById('addCommentButton');
				openPageCommentEditorButton && openPageCommentEditorButton.click();
			},
			// intentionally not localized because Loom RecordSDK does not support i18n
			insertButtonText: 'Add to comment',
		};
	} else {
		postLoomRecordingCTA = PostLoomRecordingCTA.COPY_LINK;
		// Not defining openLoomRecorderParams here so that
		// undefined will be passed to the Loom SDK resulting
		// in the Loom SDK rendering a single CTA of 'Copy link'
	}

	const { openLoomRecorder, isOpeningRecorder, isLoomRecorderInitialized, hasRecordingAccess } =
		useLoomRecorderEntryPoint({
			entryPointLocation: 'pageHeader',
			flags,
			resetInsertFunctionOnUnmount,
		});

	const shouldActivateLoomGAOnboarding =
		entryPointVariant === 'CO_USE' && hasRecordingAccess && isLoomRecorderInitialized;

	const [isLoomGAOnboardingActive, stopLoomGAOnboarding] = useSkippableCoordination(
		'cc-loom-onboarding-spotlight-editor',
		!(shouldActivateLoomGAOnboarding && fg('confluence_m25_onboarding_spotlights')),
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createLoomHeaderItem',
				source,
				attributes: {
					postLoomRecordingCTA,
					contentType,
					pageMode,
				},
			},
		}).fire();

		void openLoomRecorder(openLoomRecorderParams);
	}, [
		createAnalyticsEvent,
		openLoomRecorder,
		openLoomRecorderParams,
		postLoomRecordingCTA,
		source,
		pageMode,
		contentType,
	]);

	const handleSpotlightClose = useCallback(() => {
		if (isLoomGAOnboardingActive) {
			void stopLoomGAOnboarding();
		}
	}, [stopLoomGAOnboarding, isLoomGAOnboardingActive]);

	const baseLoomButton = (
		<Tooltip content={label} position="left">
			{(tooltipProps) => (
				<ButtonSpacer>
					{isCircle ? (
						<IconButton
							label={label}
							{...tooltipProps}
							isLoading={isOpeningRecorder}
							onClick={handleClick}
							appearance="subtle"
							shape="circle"
							icon={LoomVideoIcon}
							testId={testId}
							isDisabled={
								isDisabled && expVal('platform_editor_offline_editing_web', 'isEnabled', false)
							}
						/>
					) : (
						<IconButton
							appearance="subtle"
							icon={LoomVideoIcon}
							label={label}
							{...tooltipProps}
							onClick={handleClick}
							isLoading={isOpeningRecorder}
							data-vc={dataVC}
							{...ssrPlaceholderIdProp}
							testId={testId}
							isDisabled={
								isDisabled && expVal('platform_editor_offline_editing_web', 'isEnabled', false)
							}
						/>
					)}
				</ButtonSpacer>
			)}
		</Tooltip>
	);

	if (
		isLoomGAOnboardingActive &&
		entryPointVariant === 'CO_USE' &&
		fg('confluence_m25_onboarding_spotlights')
	) {
		return (
			<PageHeaderLoomButtonSpotlight
				onTryClick={handleClick}
				onClose={handleSpotlightClose}
				isCircle={isCircle}
			>
				{baseLoomButton}
			</PageHeaderLoomButtonSpotlight>
		);
	}

	return baseLoomButton;
};

export const PageHeaderLoomButton = withFlags(PageHeaderLoomButtonComponent);
